import * as THREE from 'three';
import type { SfxName } from '../content/types';
import type { Controls } from '../core/device';
import { clamp, damp, lerp } from '../core/math';
import type { Random } from '../core/Random';
import type { Player } from '../entities/Player';
import { buildRifle } from '../world/builders/character';
import type { ParticleSystem } from '../world/Particles';
import { setUp, type PopTarget, type Range } from '../world/range';
import type { Stage, StageButton, StageHud, StageInput } from './types';

export const RANGE_SHELLS = 12;
export const RANGE_TIME = 40;
const BASE_FOV = 55;
const SCOPE_FOV = 16;
/** Seconds to work the lever between shots. */
const CYCLE = 0.55;
const EYE = 1.6;

export interface ShootingFx {
  sparks: ParticleSystem;
  smoke: ParticleSystem;
  sfx(name: SfxName): void;
  float(text: string, at: THREE.Vector3, color: string): void;
}

/**
 * Bubba's Gun Range in first person: you're at a lane in the shed with a lever-action
 * rifle. Steel plates flip up out of the dirt and drop again, a possum runs the cable,
 * Gus throws clays and there's a rail of cans. Mouse (or a finger) aims, the crosshair
 * breathes with you, and the more you've had to drink the more it wanders.
 */
export class ShootingStage implements Stage {
  readonly title = "Bubba's Gun Range";
  readonly crosshair = true;
  readonly pointerLock = true;
  score = 0;
  hits = 0;
  shells = RANGE_SHELLS;
  private t = 0;
  private endAt = -1;
  private yaw = 0;
  private pitch = -0.03;
  private recoil = 0;
  private cycle = 0;
  /** When the lever's worked after a shot (for its clack). */
  private leverAt = -1;
  private breath = 1;
  private winded = 0;
  private holding = false;
  private scopeAmt = 0;
  private nextPop = 1.4;
  private runner = { t: -1, dir: 1, at: [5.5, 17, 29] };
  private clay: { pos: THREE.Vector3; vel: THREE.Vector3 } | null = null;
  private clayAt = [9.5, 21.5, 33];
  private readonly bigfootAt: number;
  private readonly sway: number;
  private readonly view: THREE.Group;
  private readonly flash: THREE.Mesh;
  private readonly flashMat: THREE.MeshBasicMaterial;
  private readonly ray = new THREE.Raycaster();
  private readonly eye = new THREE.Vector3();
  private readonly dir = new THREE.Vector3();
  private readonly tmp = new THREE.Vector3();
  private readonly lane: THREE.Vector3;
  private camera: THREE.PerspectiveCamera | null = null;
  private baseFov = 60;
  private banner: { text: string; color: string; until: number } | null = null;

  constructor(
    private readonly range: Range,
    private readonly player: Player,
    /** Follows the camera; drawn over the world (see Game's view scene). */
    private readonly viewRoot: THREE.Object3D,
    private readonly rng: Random,
    private readonly fx: ShootingFx,
    buzz: number,
  ) {
    this.lane = rng.pick(range.lanes);
    this.bigfootAt = rng.range(13, 30);
    this.sway = 0.004 + clamp((buzz - 25) / 75, 0, 1) * 0.022;
    // The view model: a bigger copy of the rifle, held low and to the right.
    this.view = new THREE.Group();
    const rifle = buildRifle(true);
    rifle.rotation.y = Math.PI;
    rifle.scale.setScalar(0.85);
    this.view.add(rifle);
    this.flashMat = new THREE.MeshBasicMaterial({
      color: '#ffd27a',
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    this.flash = new THREE.Mesh(new THREE.IcosahedronGeometry(0.07, 0), this.flashMat);
    this.flash.position.set(0, 0.03, -0.85);
    this.view.add(this.flash);
    this.view.position.set(0.15, -0.165, -0.4);
    this.view.rotation.set(0.02, 0.07, 0);
    this.view.traverse((o) => (o.castShadow = false));
  }

  get done(): boolean {
    return this.endAt >= 0 && this.t - this.endAt > 1.4;
  }

  get scope(): number {
    return this.scopeAmt;
  }

  hint(controls: Controls): string {
    if (this.endAt >= 0) return 'Cease fire!';
    return {
      keys: 'Mouse to aim · click or Space to fire · right-click or Z to scope · hold Shift to steady your breath',
      touch: 'Drag to aim · FIRE to shoot · :scope: scope · :lungs: hold your breath to steady up',
      pad: 'Right stick to aim · RT to fire · LT to scope · hold LB to steady your breath',
    }[controls];
  }

  buttons(touch: boolean): StageButton[] {
    if (!touch) return [];
    return [
      { code: 'Space', label: 'FIRE', primary: true },
      { code: 'Alt', label: ':scope:', toggle: true },
      { code: 'ShiftLeft', label: ':lungs:' },
    ];
  }

  begin(): void {
    const { player, lane } = this;
    this.range.reset();
    player.teleport(lane.x, lane.z, Math.PI);
    player.scripted = true;
    player.viewHidden = true;
    this.viewRoot.add(this.view);
    player.anim.update({ dt: 1, time: 0, speed: 0, pose: 'aim' });
  }

  update(dt: number, input: StageInput): void {
    this.t += dt;
    this.player.anim.update({ dt, time: this.t, speed: 0, pose: 'aim' });
    this.updateTargets(dt);
    if (this.endAt >= 0) return;

    // Aim. Scoped in, the same mouse movement turns the view less.
    const fov = lerp(BASE_FOV, SCOPE_FOV, this.scopeAmt);
    const k = 0.0021 * (fov / BASE_FOV);
    this.yaw = clamp(this.yaw - input.lookX * k, -1.05, 1.05);
    this.pitch = clamp(this.pitch - input.lookY * k, -0.35, 0.6);
    const keyAim = 0.9 * dt * (fov / BASE_FOV);
    if (input.down.has('ArrowLeft') || input.down.has('KeyA')) this.yaw = Math.min(1.05, this.yaw + keyAim);
    if (input.down.has('ArrowRight') || input.down.has('KeyD')) this.yaw = Math.max(-1.05, this.yaw - keyAim);
    if (input.down.has('ArrowUp') || input.down.has('KeyW')) this.pitch = Math.min(0.6, this.pitch + keyAim);
    if (input.down.has('ArrowDown') || input.down.has('KeyS'))
      this.pitch = Math.max(-0.35, this.pitch - keyAim);
    const scoped = input.alt || input.down.has('KeyZ');
    this.scopeAmt = damp(this.scopeAmt, scoped ? 1 : 0, 12, dt);

    // Hold your breath to steady up; hold it too long and you're gasping.
    const wantHold = input.down.has('ShiftLeft') || input.down.has('ShiftRight');
    this.winded = Math.max(0, this.winded - dt);
    this.holding = wantHold && this.breath > 0 && this.winded <= 0;
    if (this.holding) {
      this.breath = Math.max(0, this.breath - dt / 4);
      if (this.breath <= 0) this.winded = 2.2;
    } else this.breath = Math.min(1, this.breath + dt / 3);

    this.recoil = damp(this.recoil, 0, 9, dt);
    this.cycle = Math.max(0, this.cycle - dt);
    if (this.leverAt >= 0 && this.t >= this.leverAt) {
      this.leverAt = -1;
      this.fx.sfx('lever');
    }
    if (input.fire && this.cycle <= 0 && this.shells > 0) this.shoot();
    if (this.shells <= 0 && this.cycle <= 0) this.stop('OUT OF AMMO', '#ffffff');
    else if (this.t >= RANGE_TIME) this.stop("TIME'S UP", '#ffffff');
  }

  private stop(text: string, color: string): void {
    if (this.endAt >= 0) return;
    this.endAt = this.t;
    this.banner = { text, color, until: this.t + 1.4 };
    for (const t of this.range.targets) t.want = 0;
  }

  /** Points the rifle straight at a spot (tests, and anything that wants to help you aim). */
  aimAt(p: THREE.Vector3): void {
    const dx = p.x - this.lane.x;
    const dz = p.z - this.lane.z;
    this.yaw = Math.atan2(-dx, -dz);
    this.pitch = Math.atan2(p.y - EYE, Math.hypot(dx, dz));
  }

  /** Where the bullet goes: straight out of the middle of the screen. */
  private aimRay(): THREE.Ray {
    const cam = this.camera;
    if (cam) {
      cam.getWorldPosition(this.eye);
      cam.getWorldDirection(this.dir);
    }
    return new THREE.Ray(this.eye.clone(), this.dir.clone());
  }

  private shoot(): void {
    const { fx } = this;
    this.shells--;
    this.cycle = CYCLE;
    this.recoil = 0.075 + this.rng.range(0, 0.02);
    this.flashMat.opacity = 1;
    fx.sfx('gunshot');
    this.leverAt = this.t + 0.26;
    const ray = this.aimRay();
    this.ray.set(ray.origin, ray.direction);
    this.ray.far = 80;

    // The clay is small and quick: anywhere close to the line counts.
    const c = this.clay;
    if (c && ray.distanceToPoint(c.pos) < 0.32) {
      this.award(3, c.pos, '#ff7a1a', 'CLAY!');
      for (let i = 0; i < 16; i++)
        fx.sparks.spawn({
          x: c.pos.x,
          y: c.pos.y,
          z: c.pos.z,
          vx: this.rng.range(-3, 3),
          vy: this.rng.range(-1, 3),
          vz: this.rng.range(-3, 3),
          life: 0.9,
          size: 0.12,
          color: '#ff7a1a',
          gravity: 9,
        });
      this.clay = null;
      this.range.clay.visible = false;
      fx.sfx('crunch');
      return;
    }

    const up = this.range.targets.filter((t) => t.up > 0.6);
    const run = this.range.runner;
    const cans = this.range.cans.filter((k) => !k.hit);
    const meshes: THREE.Object3D[] = [
      ...up.map((t) => t.hit),
      ...(run.root.visible ? [run.hit] : []),
      ...cans.map((k) => k.mesh),
    ];
    const hit = this.ray.intersectObjects(meshes, false)[0];
    if (!hit) {
      this.miss(ray);
      return;
    }
    const p = hit.point;
    const target = up.find((t) => t.hit === hit.object);
    if (target) {
      this.award(target.points, p, '#ffd400', target.kind === 'bigfoot' ? 'BIGFOOT!!' : undefined);
      target.want = 0;
      target.until = 0;
      this.spark(p, '#ffe9a0');
      fx.sfx('ting');
      return;
    }
    if (hit.object === run.hit) {
      this.award(3, p, '#9dff6a', 'GOT HIM!');
      run.root.visible = false;
      this.runner.t = -1;
      this.spark(p, '#ffe9a0');
      fx.sfx('ting');
      return;
    }
    const can = cans.find((k) => k.mesh === hit.object);
    if (can) {
      can.hit = true;
      can.vel.set(this.rng.range(-1.5, 1.5), this.rng.range(3, 5), -this.rng.range(2, 4));
      can.spin.set(this.rng.range(-12, 12), this.rng.range(-6, 6), this.rng.range(-12, 12));
      this.award(1, p, '#ffffff');
      fx.sfx('clank');
    }
  }

  private award(points: number, at: THREE.Vector3, color: string, shout?: string): void {
    this.score += points;
    this.hits++;
    this.fx.float(`+${points}${shout ? ` ${shout}` : ''}`, at.clone().setY(at.y + 0.4), color);
  }

  private spark(p: THREE.Vector3, color: string): void {
    for (let i = 0; i < 10; i++)
      this.fx.sparks.spawn({
        x: p.x,
        y: p.y,
        z: p.z,
        vx: this.rng.range(-2, 2),
        vy: this.rng.range(0, 3),
        vz: this.rng.range(0, 2),
        life: 0.35,
        size: 0.08,
        color,
        gravity: 6,
      });
  }

  /** A puff of dirt where the round went: the ground, or the berm at the back. */
  private miss(ray: THREE.Ray): void {
    let d = Infinity;
    if (ray.direction.y < -0.001) d = -ray.origin.y / ray.direction.y;
    const berm = -102.5;
    if (ray.direction.z < -0.001) d = Math.min(d, (berm - ray.origin.z) / ray.direction.z);
    if (!Number.isFinite(d) || d > 80) return;
    const p = ray.at(d, this.tmp);
    for (let i = 0; i < 8; i++)
      this.fx.smoke.spawn({
        x: p.x,
        y: Math.max(0.05, p.y),
        z: p.z,
        vx: this.rng.range(-0.6, 0.6),
        vy: this.rng.range(0.8, 2),
        vz: this.rng.range(-0.3, 0.6),
        life: 0.8,
        size: 0.3,
        grow: 2,
        color: '#8a6a48',
      });
  }

  private updateTargets(dt: number): void {
    const { range, t, rng } = this;
    const active = this.endAt < 0;
    // Pop-ups: keep a few standing at a time, each for a moment.
    if (active) {
      this.nextPop -= dt;
      const standing = range.targets.filter((x) => x.want > 0).length;
      if (this.nextPop <= 0 && standing < 3) {
        this.nextPop = rng.range(0.6, 1.3);
        const down = range.targets.filter((x) => x.want === 0 && x.up < 0.05 && x.kind !== 'bigfoot');
        if (down.length) this.raise(rng.pick(down));
      }
      const bigfoot = range.targets.find((x) => x.kind === 'bigfoot');
      if (bigfoot && t >= this.bigfootAt && t - dt < this.bigfootAt) {
        this.raise(bigfoot, 1.7);
        this.banner = { text: 'IS THAT...', color: '#9dff6a', until: t + 1.2 };
      }
    }
    for (const x of range.targets) {
      if (x.want > 0 && t > x.until) x.want = 0;
      const next = damp(x.up, x.want, x.want > x.up ? 14 : 9, dt);
      if (Math.abs(next - x.up) > 1e-4) setUp(x, next);
    }

    // The running possum: three passes along the cable.
    const r = range.runner;
    if (active && this.runner.t < 0 && this.runner.at.length && t >= (this.runner.at[0] as number)) {
      this.runner.at.shift();
      this.runner.t = 0;
      this.runner.dir = rng.chance(0.5) ? 1 : -1;
      r.root.visible = true;
    }
    if (this.runner.t >= 0) {
      this.runner.t += dt;
      const span = r.toX - r.fromX;
      const f = (this.runner.t * 5.2) / span;
      if (f >= 1 || !active) {
        this.runner.t = -1;
        r.root.visible = false;
      } else {
        r.root.position.x = this.runner.dir > 0 ? r.fromX + f * span : r.toX - f * span;
        r.root.rotation.y = this.runner.dir > 0 ? Math.PI : 0;
        r.root.position.y = Math.abs(Math.sin(this.runner.t * 14)) * 0.05;
      }
    }

    // Clays from the trap house.
    if (active && !this.clay && this.clayAt.length && t >= (this.clayAt[0] as number)) {
      this.clayAt.shift();
      this.clay = {
        pos: range.trap.clone(),
        vel: new THREE.Vector3(-rng.range(9, 12), rng.range(9, 11), -rng.range(1, 3)),
      };
      range.clay.visible = true;
      this.fx.sfx('boing');
    }
    if (this.clay) {
      const c = this.clay;
      c.vel.y -= 9.8 * dt;
      c.pos.addScaledVector(c.vel, dt);
      range.clay.position.copy(c.pos);
      range.clay.rotation.y += dt * 20;
      if (c.pos.y < 0.05) {
        this.clay = null;
        range.clay.visible = false;
      }
    }

    // Cans that got hit tumble off the rail.
    for (const can of range.cans) {
      if (!can.hit) continue;
      const m = can.mesh;
      if (m.position.y <= 0.06 && can.vel.y <= 0) {
        m.position.y = 0.06;
        m.rotation.x = Math.PI / 2;
        continue;
      }
      can.vel.y -= 9.8 * dt;
      m.position.addScaledVector(can.vel, dt);
      m.rotation.x += can.spin.x * dt;
      m.rotation.z += can.spin.z * dt;
    }
  }

  private raise(x: PopTarget, life?: number): void {
    x.want = 1;
    // Far plates don't stay up as long.
    const far = -x.root.position.z - 85;
    x.until = this.t + (life ?? this.rng.range(1.7, 2.8) - far * 0.06);
    this.fx.sfx('clank');
  }

  frame(camera: THREE.PerspectiveCamera, dt: number): void {
    if (this.camera !== camera) {
      this.camera = camera;
      this.baseFov = camera.fov;
    }
    const t = this.t;
    let a = this.sway * (this.holding ? 0.15 : this.winded > 0 ? 2.4 : 1);
    if (this.scopeAmt > 0.5) a *= 1.3;
    const sy = Math.sin(t * 0.83) * a + Math.sin(t * 2.1) * a * 0.35;
    const sp = Math.sin(t * 1.17 + 1) * a * 0.8 + Math.sin(t * 2.9) * a * 0.25;
    const yaw = this.yaw + sy;
    const pitch = this.pitch + sp + this.recoil;
    camera.position.set(this.lane.x, EYE, this.lane.z);
    const cp = Math.cos(pitch);
    this.tmp.set(this.lane.x - Math.sin(yaw) * cp, EYE + Math.sin(pitch), this.lane.z - Math.cos(yaw) * cp);
    camera.lookAt(this.tmp);
    const fov = lerp(BASE_FOV, SCOPE_FOV, this.scopeAmt);
    if (Math.abs(camera.fov - fov) > 0.01) {
      camera.fov = fov;
      camera.updateProjectionMatrix();
    }
    // The rifle kicks, the lever cycles, and it's out of the way when you're scoped.
    this.view.visible = this.scopeAmt < 0.6;
    const lever = this.cycle > 0 ? Math.sin((1 - this.cycle / CYCLE) * Math.PI) : 0;
    this.view.position.set(0.15, -0.165 - lever * 0.05, -0.4 + this.recoil * 1.4);
    this.view.rotation.set(0.02 + this.recoil * 2.2 - lever * 0.25, 0.07, lever * 0.15);
    this.flashMat.opacity = damp(this.flashMat.opacity, 0, 30, dt);
    this.flash.scale.setScalar(0.6 + this.flashMat.opacity);
  }

  hud(): StageHud {
    const left = Math.max(0, Math.ceil(RANGE_TIME - this.t));
    const b = this.banner && this.t < this.banner.until ? this.banner : null;
    const meters = [];
    if (this.breath < 0.999 || this.holding)
      meters.push({ label: ':lungs:', value: this.breath, color: this.winded > 0 ? '#ff6b6b' : '#9fdcff' });
    return {
      score: `${this.score} pts`,
      status: `${'▮'.repeat(this.shells)}${'▯'.repeat(RANGE_SHELLS - this.shells)}  ·  ${left}s`,
      meters,
      banner: b ? { text: b.text, color: b.color } : null,
    };
  }

  end(): void {
    const cam = this.camera;
    if (cam) {
      cam.fov = this.baseFov;
      cam.updateProjectionMatrix();
    }
    this.view.removeFromParent();
    this.flash.geometry.dispose();
    this.flashMat.dispose();
    this.range.reset();
    this.player.scripted = false;
    this.player.viewHidden = false;
    this.player.syncTransform();
  }
}
